import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Grid, IconButton, MenuItem, TextField } from '@material-ui/core';
import { AiOutlineDelete } from 'react-icons/ai';

import RequestPageLayout from '../common/request-page-layout';
import RequestSection from '../common/request-section';
import RequestInput from '../common/request-input';
import AgentEntityAutocomplete from '../common/agent-entity-autocomplete';
import { validateInvoiceRequestForm } from '../common/validators';
import {
  VAT_RATE_BY_OPTION,
  VatOption,
  currencyOptions,
  paymentTermsOptions,
  recurringDayOptions,
  recurringOptions,
  vatInclusionOptions,
  vatOptions,
} from '../common/options';
import { getMyClients } from '../../../api/requests/client';
import { createInvoiceRequest } from '../../../api/requests/requests';
import { getErrorMessage } from '../../../shared/services/error-handler';
import { Routes } from '../../../api/routes';

import '../Requests.scss';

const defaultFormData = {
  payment_terms: { value: '', error: false },
  is_recurring: { value: '', error: false },
  recurring_day: { value: '', error: false },
  currency: { value: '', error: false },
  vat_inclusion: { value: '', error: false },
  notes: { value: '', error: false },
  services_description: { value: '', error: false },
};

const buildEmptyLine = () => ({
  key: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  description: '',
  quantity: '1',
  unit_price: '',
  vat_option: VatOption.standard18,
});

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

const InvoiceRequest = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const [formData, setFormData] = useState(defaultFormData);
  const [client, setClient] = useState(null);
  const [lineItems, setLineItems] = useState([buildEmptyLine()]);
  const [submitting, setSubmitting] = useState(false);

  const totals = useMemo(() => {
    const subtotal = lineItems.reduce(
      (sum, item) => sum + toNumber(item.quantity) * toNumber(item.unit_price),
      0
    );

    const vat = lineItems.reduce((sum, item) => {
      const lineTotal = toNumber(item.quantity) * toNumber(item.unit_price);

      return sum + (lineTotal * VAT_RATE_BY_OPTION[item.vat_option]) / 100;
    }, 0);

    return { subtotal, vat, total: subtotal + vat };
  }, [lineItems]);

  const updateLine = (key, changes) =>
    setLineItems((current) =>
      current.map((item) => (item.key === key ? { ...item, ...changes } : item))
    );

  const removeLine = (key) =>
    setLineItems((current) => current.filter((item) => item.key !== key));

  const onSubmit = async () => {
    if (!validateInvoiceRequestForm(formData, setFormData, lineItems)) {
      enqueueSnackbar(t('fill-required-fields'), { variant: 'warning' });
      return;
    }

    setSubmitting(true);

    try {
      await createInvoiceRequest({
        client_id: client?.id || undefined,
        payment_terms: formData.payment_terms.value || undefined,
        is_recurring: formData.is_recurring.value === 'yes',
        recurring_day:
          formData.is_recurring.value === 'yes'
            ? Number(formData.recurring_day.value)
            : undefined,
        currency: formData.currency.value,
        vat_inclusion: formData.vat_inclusion.value || undefined,
        services_description: formData.services_description.value,
        notes: formData.notes.value || undefined,
        items: lineItems.map((item) => ({
          description: item.description,
          quantity: toNumber(item.quantity),
          unit_price: toNumber(item.unit_price),
          vat_option: item.vat_option,
        })),
      });

      enqueueSnackbar(t('invoice-request-submitted'), { variant: 'success' });
      history.push(Routes.myRequests);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error) || t('something-went-wrong'), {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RequestPageLayout
      tTitle="invoice-request-title"
      tSubtitle="invoice-request-page-subtitle"
      tSubmitLabel="submit-invoice-request"
      submitClassName="invoice"
      onSubmit={onSubmit}
      submitting={submitting}
    >
      <RequestSection tTitle="who-is-the-client">
        <AgentEntityAutocomplete
          fetchOptions={getMyClients}
          labelKey="client_name"
          tPlaceholder="search-client-placeholder"
          tEmptyMessage="no-clients-linked-to-you"
          value={client}
          onSelect={setClient}
        />
      </RequestSection>

      <RequestSection tTitle="invoice-details">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <RequestInput
              field="payment_terms"
              tLabel="payment-terms-label"
              tPlaceholder="select-payment-terms"
              formData={formData}
              setFormData={setFormData}
              options={paymentTermsOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RequestInput
              field="is_recurring"
              tLabel="is-recurring-invoice"
              required
              tHelper="is-recurring-invoice-helper"
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
              options={recurringOptions}
              onValueChange={(value) => {
                if (value !== 'yes') {
                  setFormData((current) => ({
                    ...current,
                    is_recurring: { value, error: false },
                    recurring_day: { value: '', error: false },
                  }));
                }
              }}
            />
          </Grid>
          {formData.is_recurring.value === 'yes' && (
            <Grid item xs={12} sm={6}>
              <RequestInput
                field="recurring_day"
                tLabel="recurring-day"
                required
                tPlaceholder="select-recurring-day"
                tError="field-is-required"
                formData={formData}
                setFormData={setFormData}
                options={recurringDayOptions}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <RequestInput
              field="currency"
              tLabel="currency"
              required
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
              options={currencyOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RequestInput
              field="vat_inclusion"
              tLabel="vat-inclusion"
              formData={formData}
              setFormData={setFormData}
              options={vatInclusionOptions}
            />
          </Grid>
          <Grid item xs={12}>
            <RequestInput
              field="notes"
              tLabel="invoice-notes"
              tPlaceholder="invoice-notes-placeholder"
              formData={formData}
              setFormData={setFormData}
              multiline
              rows={4}
            />
          </Grid>
        </Grid>
      </RequestSection>

      <RequestSection tTitle="invoice-line-items">
        <RequestInput
          field="services_description"
          tLabel="description-of-services"
          required
          tPlaceholder="description-of-services-placeholder"
          tHelper="description-of-services-helper"
          tError="field-is-required"
          formData={formData}
          setFormData={setFormData}
          multiline
          rows={4}
        />

        <div className="request-line-items">
          <div className="request-line-items-head">
            <span className="request-line-cell-description">
              {t('line-description')}
            </span>
            <span className="request-line-cell-quantity">
              {t('line-quantity')}
            </span>
            <span className="request-line-cell-price">
              {t('line-unit-price')}
            </span>
            <span className="request-line-cell-vat">{t('line-vat')}</span>
            <span className="request-line-cell-total">
              {t('line-total')}
            </span>
          </div>

          {lineItems.map((item) => (
            <div className="request-line-item" key={item.key}>
              <TextField
                className="request-line-cell-description"
                variant="outlined"
                placeholder={t('line-description-placeholder')}
                value={item.description}
                onChange={(event) =>
                  updateLine(item.key, { description: event.target.value })
                }
              />
              <TextField
                className="request-line-cell-quantity"
                variant="outlined"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                value={item.quantity}
                onChange={(event) =>
                  updateLine(item.key, { quantity: event.target.value })
                }
              />
              <TextField
                className="request-line-cell-price"
                variant="outlined"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                placeholder="0.00"
                value={item.unit_price}
                onChange={(event) =>
                  updateLine(item.key, { unit_price: event.target.value })
                }
              />
              <TextField
                className="request-line-cell-vat"
                variant="outlined"
                select
                value={item.vat_option}
                onChange={(event) =>
                  updateLine(item.key, { vat_option: event.target.value })
                }
              >
                {vatOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.tLabel)}
                  </MenuItem>
                ))}
              </TextField>
              <span className="request-line-cell-total">
                {(
                  toNumber(item.quantity) * toNumber(item.unit_price)
                ).toFixed(2)}
              </span>
              {lineItems.length > 1 && (
                <IconButton
                  className="request-line-cell-remove"
                  size="small"
                  aria-label={t('delete')}
                  onClick={() => removeLine(item.key)}
                >
                  <AiOutlineDelete size={18} />
                </IconButton>
              )}
            </div>
          ))}

          <button
            type="button"
            className="request-add-line-button"
            onClick={() =>
              setLineItems((current) => [...current, buildEmptyLine()])
            }
          >
            {t('add-line')}
          </button>

          <div className="request-totals">
            <div className="request-total-row">
              <span className="request-total-amount">
                {totals.subtotal.toFixed(2)}
              </span>
              <span>{t('subtotal-excl-vat')}</span>
            </div>
            <div className="request-total-row grand">
              <span className="request-total-amount">
                {totals.total.toFixed(2)}
              </span>
              <span>{t('total-payable')}</span>
            </div>
          </div>
        </div>
      </RequestSection>
    </RequestPageLayout>
  );
};

export default InvoiceRequest;
